import React, { useMemo, useState } from "react";
import {
  Card,
  Empty,
  Spin,
  Table,
  Typography,
  Button,
  Popconfirm,
  Space,
  message,
} from "antd";
import { CheckSquareOutlined, CloseOutlined } from "@ant-design/icons";
import { useOpenRoute } from "../../utils/openRouteUtils";
import {
  activateAccounts,
  deactivateAccounts,
} from "../../api/bankAccountApi";

const { Title } = Typography;

function InstitutionAccountsTable({
  title = "Bank Accounts",
  subtitle = "",
  loading = false,
  accounts = [],
  createColumns,
  columns = [],
  fetchData,
  enableStatusChange = true,
}) {
  const openRoute = useOpenRoute();

  const [statusChangeMode, setStatusChangeMode] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);

  const selectedAccounts = useMemo(() => {
    return accounts.filter((account) => selectedRowKeys.includes(account.id));
  }, [accounts, selectedRowKeys]);

  const hasSelected = selectedAccounts.length > 0;

  const inactiveSelectedIds = useMemo(() => {
    return selectedAccounts
      .filter((account) => !Boolean(account?.is_active))
      .map((account) => account.id);
  }, [selectedAccounts]);

  const activeSelectedIds = useMemo(() => {
    return selectedAccounts
      .filter((account) => Boolean(account?.is_active))
      .map((account) => account.id);
  }, [selectedAccounts]);

  const showActivateButton = inactiveSelectedIds.length > 0;
  const showDeactivateButton = activeSelectedIds.length > 0;

  const tableColumns = useMemo(() => {
    const baseColumns =
      typeof createColumns === "function"
        ? createColumns({ bulkSelectMode: statusChangeMode })
        : columns;

    if (!statusChangeMode) return baseColumns;

    return baseColumns.filter(
      (column) =>
        column.key !== "actions" &&
        column.key !== "activeAction" &&
        column.key !== "statusAction"
    );
  }, [createColumns, columns, statusChangeMode]);

  function handleRowClick(record, event) {
    if (enableStatusChange && statusChangeMode) return;

    const target = event.target.closest(
      ".ant-checkbox-wrapper, .ant-btn, .ant-popconfirm"
    );

    if (target) return;
    if (!record?.id) return;

    openRoute(`/account/${record.id}`, event);
  }

  function handleToggleStatusChangeMode() {
    setStatusChangeMode((prev) => {
      const next = !prev;

      if (!next) {
        setSelectedRowKeys([]);
      }

      return next;
    });
  }

  const rowSelection = enableStatusChange && statusChangeMode
    ? {
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys),
        preserveSelectedRowKeys: false,
      }
    : undefined;

  async function handleActivateSelected() {
    if (!inactiveSelectedIds.length) {
      message.info("Сонгосон дансууд бүгд идэвхтэй байна");
      return;
    }

    try {
      setBulkLoading(true);

      await activateAccounts(inactiveSelectedIds);

      message.success("Идэвхгүй дансууд идэвхжлээ");
      setSelectedRowKeys([]);

      await fetchData?.();
    } catch (error) {
      message.error(error.message || "Үйлдэл амжилтгүй боллоо");
    } finally {
      setBulkLoading(false);
    }
  }

  async function handleDeactivateSelected() {
    if (!activeSelectedIds.length) {
      message.info("Сонгосон дансууд бүгд идэвхгүй байна");
      return;
    }

    try {
      setBulkLoading(true);

      await deactivateAccounts(activeSelectedIds);

      message.success("Идэвхтэй дансууд идэвхгүй боллоо");
      setSelectedRowKeys([]);

      await fetchData?.();
    } catch (error) {
      message.error(error.message || "Үйлдэл амжилтгүй боллоо");
    } finally {
      setBulkLoading(false);
    }
  }

  return (
    <Card className="rounded-2xl shadow-md">
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <Title level={4} className="!mb-1">
            {title}
          </Title>

          {subtitle ? (
            <div className="text-sm text-slate-500">{subtitle}</div>
          ) : null}
        </div>

        {enableStatusChange && (
          <Space wrap>
            {statusChangeMode && hasSelected && (
              <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2">
                <span className="text-sm font-semibold text-slate-700">
                  {selectedRowKeys.length} данс сонгогдсон
                </span>

                <Space>
                  {showActivateButton && (
                    <Popconfirm
                      title="Сонгосон идэвхгүй дансуудыг идэвхжүүлэх үү?"
                      okText="Тийм"
                      cancelText="Үгүй"
                      onConfirm={handleActivateSelected}
                    >
                      <Button loading={bulkLoading} className="rounded-xl">
                        Идэвхжүүлэх
                      </Button>
                    </Popconfirm>
                  )}

                  {showDeactivateButton && (
                    <Popconfirm
                      title="Сонгосон идэвхтэй дансуудыг идэвхгүй болгох уу?"
                      okText="Тийм"
                      cancelText="Үгүй"
                      onConfirm={handleDeactivateSelected}
                    >
                      <Button danger loading={bulkLoading} className="rounded-xl">
                        Идэвхгүй болгох
                      </Button>
                    </Popconfirm>
                  )}
                </Space>
              </div>
            )}

            <Button
              icon={statusChangeMode ? <CloseOutlined /> : <CheckSquareOutlined />}
              type={statusChangeMode ? "default" : "primary"}
              className="rounded-xl"
              onClick={handleToggleStatusChangeMode}
            >
              {statusChangeMode ? "Xаах" : "Дансны төлөв солих"}
            </Button>
          </Space>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Spin />
        </div>
      ) : accounts.length === 0 ? (
        <Empty description="No bank accounts found" />
      ) : (
        <Table
          rowKey="id"
          rowSelection={rowSelection}
          columns={tableColumns}
          dataSource={accounts}
          pagination={false}
          scroll={{ x: 1500 }}
          onRow={(record) => ({
            onClick: (event) => handleRowClick(record, event),
            className: enableStatusChange && statusChangeMode ? "" : "cursor-pointer",
          })}
        />
      )}
    </Card>
  );
}

export default InstitutionAccountsTable;